<template>
  <div class="interest-free-operation" v-loading="loading">
    <el-form
      ref="form"
      :model="formModel"
      :rules="formRules"
      label-width="150px"
      label-position="left"
    >
      <co-fold-block title="活动信息">
        <el-form-item label="活动名称" prop="activity_name">
          <el-input
            v-model="formModel.activity_name"
            placeholder="请输入活动名称"
            :disabled="!canEditNameAndTime"
            style="max-width: 480px"
          />
        </el-form-item>
        <el-form-item label="活动时间" prop="activity_date">
          <el-date-picker
            v-model="formModel.activity_date"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            :disabled="!canEditNameAndTime"
          />
        </el-form-item>
        <el-form-item label="活动归属" prop="owner_type">
          <el-radio-group v-model="formModel.owner_type" :disabled="!canEditBaseFields">
            <el-radio label="platform">平台</el-radio>
            <el-radio label="dealer">经销商</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="可免息分期期数" prop="installment_terms">
          <el-checkbox-group v-model="formModel.installment_terms" :disabled="!canEditBaseFields">
            <el-checkbox
              v-for="item in PeriodOptions"
              :key="item.value"
              :label="item.value"
            >{{ item.label }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="免息订单金额上下限" required>
          <div class="amount-range">
            <el-form-item prop="min_order_amount" class="inline-item">
              <span class="amount-label">下限</span>
              <el-input-number
                v-model="formModel.min_order_amount"
                :precision="2"
                :min="100"
                :max="300000"
                :controls="false"
                :disabled="!canEditCreateOnlyAmountFields"
              />
              <span class="amount-unit">元</span>
            </el-form-item>
            <el-form-item prop="max_order_amount" class="inline-item">
              <span class="amount-label">上限</span>
              <el-input-number
                v-model="formModel.max_order_amount"
                :precision="2"
                :min="100"
                :max="300000"
                :controls="false"
                :disabled="!canEditCreateOnlyAmountFields"
              />
              <span class="amount-unit">元</span>
            </el-form-item>
          </div>
          <p class="form-tip">在该金额范围内的订单用户可免息。金额最低100.00元，最高300000.00元</p>
        </el-form-item>
        <el-form-item label="活动预算金额" prop="budget_amount">
          <el-input-number
            v-model="formModel.budget_amount"
            :precision="2"
            :min="0"
            :controls="false"
            placeholder="请输入预算金额"
            :disabled="!canEditCreateOnlyAmountFields"
          />
        </el-form-item>
        <el-form-item label="预算提醒金额" prop="budget_alert_amount">
          <el-input-number
            v-model="formModel.budget_alert_amount"
            :precision="2"
            :min="0"
            :controls="false"
            placeholder="请输入预算提醒金额"
            :disabled="!canEditCreateOnlyAmountFields"
          />
        </el-form-item>
      </co-fold-block>

      <co-fold-block title="活动参与商品">
        <el-form-item label="活动参与商品">
          <div class="selector-block">
            <ImportDialog
              v-if="canEditProductsStores"
              buttonName="导入商品"
              buttonType=""
              fileName="活动参与商品导入模板"
              fileType="pcredit_activity_skus"
              title="导入商品"
              @uploadSuccess="handleImportSkuSuccess"
            />
            <span v-if="skuList.length" class="selector-count">已添加 {{ skuList.length }} 个商品</span>
            <template v-if="skuList.length">
              <el-link type="primary" :underline="false" @click="skuPreviewVisible = true">查看</el-link>
              <el-link
                v-if="canEditProductsStores"
                type="primary"
                :underline="false"
                @click="clearSkus"
              >清除</el-link>
            </template>
          </div>
          <p class="form-tip">指定分期商品参与免息分期活动，未添加商品为全部分期商品参与</p>
        </el-form-item>
      </co-fold-block>

      <co-fold-block title="活动参与经销商">
        <el-form-item label="活动参与经销商" prop="dealers">
          <div class="selector-block">
            <el-button
              type="primary"
              plain
              :disabled="!canEditDealers"
              @click="openDealerSelect"
            >选择经销商</el-button>
            <span v-if="formModel.dealers.length" class="selector-count">已添加 {{ formModel.dealers.length }} 家经销商</span>
            <template v-if="formModel.dealers.length">
              <el-link type="primary" :underline="false" @click="dealerPreviewVisible = true">查看</el-link>
              <el-link
                v-if="canEditDealers"
                type="primary"
                :underline="false"
                @click="clearDealers"
              >清除</el-link>
            </template>
          </div>
          <p class="form-tip">经销商清单仅展示已开启花呗分期，并已配置二级商户名称/ID、费率、商贴预算提醒邮箱及手机的经销商</p>
        </el-form-item>
      </co-fold-block>

      <co-fold-block title="活动参与门店">
        <el-form-item label="活动参与门店" prop="store_type">
          <el-radio-group v-model="formModel.store_type" :disabled="!canEditProductsStores">
            <el-radio label="all">经销商下全部门店</el-radio>
            <el-radio label="partial">经销商下指定门店</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="formModel.store_type === 'partial'" label="指定门店" prop="stores">
          <div class="selector-block">
            <el-button
              type="primary"
              plain
              :disabled="!canEditProductsStores"
              @click="openStoreSelect"
            >选择门店</el-button>
            <span v-if="formModel.stores.length" class="selector-count">已添加 {{ formModel.stores.length }} 家门店</span>
            <template v-if="formModel.stores.length">
              <el-link type="primary" :underline="false" @click="storePreviewVisible = true">查看</el-link>
              <el-link
                v-if="canEditProductsStores"
                type="primary"
                :underline="false"
                @click="clearStores"
              >清除</el-link>
            </template>
          </div>
          <div class="import-actions">
            <ImportDialog
              v-if="canEditProductsStores && formModel.dealers.length"
              buttonName="导入门店"
              buttonType=""
              fileName="活动参与门店导入模板"
              fileType="pcredit_activity_stores"
              title="导入门店"
              :customParams="storeImportCustomParams"
              @uploadSuccess="handleImportStoreSuccess"
            />
            <el-button
              v-else
              plain
              :disabled="!canEditProductsStores"
              @click="handleImportStore"
            >导入门店</el-button>
          </div>
          <p class="form-tip">门店清单仅展示开启了分期的经销商下，并同时开启了分期的门店</p>
        </el-form-item>
      </co-fold-block>
    </el-form>

    <FooterSubmitButtons
      v-if="!isReadonly"
      submit-text="创建活动"
      :show-draft="!id"
      :loading="submitting"
      :draft-loading="draftSubmitting"
      @cancel="handleCancel"
      @draft="handleSaveDraft"
      @submit="handleSubmit"
    />

    <DealerSelect
      :store-visible="dealerVisible"
      :is-valid="true"
      :rel-data-ids="formModel.dealers"
      :get-status="dealerSelectStatus"
      :query-params="dealerSelectQueryParams"
      @chooseStore="handleDealerSelected"
      @closeStoreDialog="dealerVisible = false"
    />
    <StoreSelect
      :store-visible="storeVisible"
      :is-valid="true"
      :rel-data-ids="formModel.stores"
      :get-status="true"
      :set-page-size="100"
      :query-params="storeSelectQueryParams"
      @chooseStore="handleStoreSelected"
      @closeStoreDialog="storeVisible = false"
    />

    <el-dialog title="已选商品" :visible.sync="skuPreviewVisible" width="720px" append-to-body>
      <el-table :data="skuList" border stripe>
        <el-table-column label="商品名称" min-width="200">
          <template slot-scope="{ row }">{{ row.item_name_snapshot || row.item_name || row.goods_name || '' }}</template>
        </el-table-column>
        <el-table-column label="商品ID" width="100">
          <template slot-scope="{ row }">{{ row.goods_id || row.default_item_id || '' }}</template>
        </el-table-column>
        <el-table-column label="SKU编码" min-width="140">
          <template slot-scope="{ row }">{{ row.sku_code || row.item_bn || '' }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
    <el-dialog title="已选经销商" :visible.sync="dealerPreviewVisible" width="720px" append-to-body>
      <el-table :data="formModel.dealers" border stripe>
        <el-table-column label="经销商名称" min-width="160">
          <template slot-scope="{ row }">{{ row.dealer_name_snapshot || row.name }}</template>
        </el-table-column>
        <el-table-column label="HuifuId" min-width="140">
          <template slot-scope="{ row }">{{ row.huifu_id || row.dealer_hqid }}</template>
        </el-table-column>
        <el-table-column prop="solution_id" label="solution_id" min-width="180" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="100" />
      </el-table>
    </el-dialog>
    <el-dialog title="已选门店" :visible.sync="storePreviewVisible" width="640px" append-to-body>
      <el-table :data="formModel.stores" border stripe>
        <el-table-column label="门店名称" min-width="160">
          <template slot-scope="{ row }">{{ row.store_name_snapshot || row.name }}</template>
        </el-table-column>
        <el-table-column label="门店编码" min-width="120">
          <template slot-scope="{ row }">{{ row.store_code || row.shop_code }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import FooterSubmitButtons from './comps/footer-submit-buttons'
import DealerSelect from '@/components/function/dealerSelect'
import StoreSelect from '@/components/storeListSelect'
import ImportDialog from '@/components/importDialog'
import { PeriodOptions } from './config'
import { getActivityDetail, createActivity, updateActivity, saveDraft, getDraft, updateDraft } from './api'

function formatTimestamp (timestamp) {
  if (!timestamp) return ''
  const ts = Number(timestamp)
  const date = ts < 1e12 ? ts * 1000 : ts
  const pad = (n) => String(n).padStart(2, '0')
  const d = new Date(date)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function amountFromApi (value) {
  if (value == null || value === '') return undefined
  return Number(value) / 100
}

function amountToApi (value) {
  if (value == null || value === '') return undefined
  return Math.round(Number(value) * 100)
}

export default {
  name: 'InterestFreeActivityOperation',
  components: {
    FooterSubmitButtons,
    DealerSelect,
    StoreSelect,
    ImportDialog
  },
  data () {
    const validatePeriods = (rule, value, callback) => {
      if (!value || !value.length) {
        callback(new Error('请至少选择一个分期期数'))
        return
      }
      callback()
    }
    const validateDealers = (rule, value, callback) => {
      if (!this.formModel.dealers.length) {
        callback(new Error('请选择活动参与经销商'))
        return
      }
      callback()
    }
    const validateStores = (rule, value, callback) => {
      if (this.formModel.store_type === 'partial' && !this.formModel.stores.length) {
        callback(new Error('请选择指定门店'))
        return
      }
      callback()
    }
    const validateMaxAmount = (rule, value, callback) => {
      if (value == null || value === '') {
        callback(new Error('请输入上限金额'))
        return
      }
      if (this.formModel.min_order_amount != null && Number(value) < Number(this.formModel.min_order_amount)) {
        callback(new Error('上限金额不能小于下限金额'))
        return
      }
      callback()
    }

    return {
      PeriodOptions,
      loading: false,
      submitting: false,
      draftSubmitting: false,
      id: null,
      draftId: null,
      skuList: [],
      dealerVisible: false,
      storeVisible: false,
      dealerSelectStatus: false,
      dealerPreviewVisible: false,
      storePreviewVisible: false,
      skuPreviewVisible: false,
      formModel: {
        activity_name: '',
        activity_date: [],
        owner_type: 'platform',
        installment_terms: [],
        min_order_amount: 100,
        max_order_amount: 300000,
        budget_amount: undefined,
        budget_alert_amount: undefined,
        store_type: 'all',
        status: 'unpublished',
        dealers: [],
        stores: [],
        skus: []
      },
      formRules: {
        activity_name: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
        activity_date: [{ required: true, message: '请选择活动时间', trigger: 'change' }],
        owner_type: [{ required: true, message: '请选择活动归属', trigger: 'change' }],
        installment_terms: [{ validator: validatePeriods, trigger: 'change' }],
        min_order_amount: [{ required: true, message: '请输入下限金额', trigger: 'blur' }],
        max_order_amount: [{ validator: validateMaxAmount, trigger: 'blur' }],
        budget_alert_amount: [{ required: true, message: '请输入预算提醒金额', trigger: 'blur' }],
        dealers: [{ validator: validateDealers, trigger: 'change' }],
        store_type: [{ required: true, message: '请选择门店范围', trigger: 'change' }],
        stores: [{ validator: validateStores, trigger: 'change' }]
      }
    }
  },
  computed: {
    isCreate () {
      return !this.id
    },
    // 详情可能返回 status 或 display_status
    activityStatus () {
      return this.formModel.display_status || this.formModel.status
    },
    isReadonly () {
      return this.activityStatus === 'ended'
    },
    // 活动归属、可免息分期期数仅创建时可改，编辑不可修改
    canEditBaseFields () {
      return this.isCreate
    },
    // 未上架：未创建 / 未发布；其余视为已上架及之后
    isUnpublished () {
      return ['unpublished', 'not_created'].includes(this.activityStatus)
    },
    // 未上架可改名称、时间；上架后不可改
    canEditNameAndTime () {
      return this.isCreate || (!this.isReadonly && this.isUnpublished)
    },
    // 经销商与名称时间一致：未上架可改，上架后不可改
    canEditDealers () {
      return this.canEditNameAndTime
    },
    // 订单金额上下限、预算相关字段创建后任意状态均不可改
    canEditCreateOnlyAmountFields () {
      return this.isCreate
    },
    // 门店、商品：未结束均可改（含已上架）
    canEditProductsStores () {
      return this.isCreate || !this.isReadonly
    },
    dealerSelectQueryParams () {
      return {
        pcredit_ready: 1,
        owner_type: this.formModel.owner_type === 'dealer' ? 'dealer' : 'platform'
      }
    },
    storeSelectQueryParams () {
      return {
        dealerId: this.formModel.dealers.map(item => Number(item.dealer_id)).filter(id => !Number.isNaN(id))
      }
    },
    storeImportCustomParams () {
      const dealerIds = this.formModel.dealers
        .map(item => Number(item.dealer_id))
        .filter(id => !Number.isNaN(id))
      return {
        dealer_ids: dealerIds.join(',')
      }
    }
  },
  mounted () {
    const draftId = this.$route.query.draft_id
    const id = this.$route.query.id
    if (draftId) {
      this.draftId = draftId
      this.initDraft()
      return
    }
    if (id) {
      this.id = id
      this.initDetail()
    }
  },
  methods: {
    normalizeSkuList (skus = []) {
      if (!Array.isArray(skus)) return []
      return skus.map((item) => ({
        ...item,
        item_id: item.item_id != null ? item.item_id : (item.sku_id != null ? item.sku_id : item.itemId),
        goods_id: item.goods_id,
        default_item_id: item.default_item_id,
        item_name_snapshot: item.item_name_snapshot || item.item_name || item.goods_name || '',
        sku_code: item.sku_code || item.item_bn || ''
      })).filter(item => item.item_id != null && item.item_id !== '')
    },
    fillFormFromDetail (data) {
      const activityDate = []
      if (data.start_time) activityDate.push(formatTimestamp(data.start_time))
      if (data.end_time) activityDate.push(formatTimestamp(data.end_time))

      const skus = this.normalizeSkuList(data.skus)
      Object.assign(this.formModel, data, {
        activity_date: activityDate,
        installment_terms: (Array.isArray(data.installment_terms)
          ? data.installment_terms
          : String(data.installment_terms || '').split(',')
        ).map(Number).filter(term => PeriodOptions.some(item => item.value === term)),
        min_order_amount: amountFromApi(data.min_order_amount) ?? this.formModel.min_order_amount,
        max_order_amount: amountFromApi(data.max_order_amount) ?? this.formModel.max_order_amount,
        budget_amount: amountFromApi(data.budget_amount),
        budget_alert_amount: amountFromApi(data.budget_alert_amount),
        store_type: data.store_scope === 'all' || (!data.store_scope && !(data.stores && data.stores.length)) ? 'all' : 'partial',
        dealers: data.dealers || [],
        stores: data.stores || [],
        skus
      })
      this.skuList = skus
    },
    fillFormFromDraft (data) {
      const form = data.form || {}
      Object.assign(this.formModel, {
        activity_name: form.activity_name || data.activity_name || '',
        activity_date: form.activity_date || [],
        owner_type: form.owner_type || 'platform',
        installment_terms: form.installment_terms || [],
        min_order_amount: form.min_order_amount != null ? form.min_order_amount : this.formModel.min_order_amount,
        max_order_amount: form.max_order_amount != null ? form.max_order_amount : this.formModel.max_order_amount,
        budget_amount: form.budget_amount,
        budget_alert_amount: form.budget_alert_amount,
        store_type: form.store_type || 'all',
        dealers: form.dealers || [],
        stores: form.stores || []
      })
      this.skuList = this.normalizeSkuList(form.skus || [])
    },
    async initDraft () {
      this.loading = true
      try {
        const res = await getDraft(this.draftId)
        const data = res.data.data
        if (!data) {
          this.$message.warning('未找到草稿')
          return
        }
        this.fillFormFromDraft(data)
      } catch (e) {
        this.$message.error('获取草稿失败')
      } finally {
        this.loading = false
      }
    },
    buildDraftBody () {
      return {
        activity_name: this.formModel.activity_name,
        form: {
          activity_name: this.formModel.activity_name,
          activity_date: this.formModel.activity_date,
          owner_type: this.formModel.owner_type,
          installment_terms: this.formModel.installment_terms,
          min_order_amount: this.formModel.min_order_amount,
          max_order_amount: this.formModel.max_order_amount,
          budget_amount: this.formModel.budget_amount,
          budget_alert_amount: this.formModel.budget_alert_amount,
          store_type: this.formModel.store_type,
          dealers: this.formModel.dealers,
          stores: this.formModel.stores,
          skus: this.skuList
        },
        payload: this.buildSubmitPayload()
      }
    },
    async handleSaveDraft () {
      if (!String(this.formModel.activity_name || '').trim()) {
        this.$message.warning('请输入活动名称')
        return
      }
      this.draftSubmitting = true
      try {
        const body = this.buildDraftBody()
        if (this.draftId) {
          await updateDraft(this.draftId, body)
        } else {
          await saveDraft(body)
        }
        this.$message.success('草稿已保存')
        this.backToList('draft')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '保存草稿失败'
        this.$message.error(msg)
      } finally {
        this.draftSubmitting = false
      }
    },
    async initDetail () {
      this.loading = true
      try {
        const res = await getActivityDetail(this.id)
        const data = res.data.data
        if (!data) {
          this.$message.warning('未找到活动数据')
          return
        }
        this.fillFormFromDetail(data)
      } catch (e) {
        this.$message.error('获取活动详情失败')
      } finally {
        this.loading = false
      }
    },
    openDealerSelect () {
      if (!this.canEditDealers) return
      this.dealerSelectStatus = true
      this.dealerVisible = true
    },
    handleDealerSelected (list) {
      this.dealerVisible = false
      const previousIds = this.formModel.dealers.map(item => Number(item.dealer_id)).sort().join(',')
      const dealers = list || []
      const nextIds = dealers.map(item => Number(item.dealer_id)).sort().join(',')
      this.formModel.dealers = dealers
      if (previousIds !== nextIds) {
        this.formModel.stores = []
      }
      this.$refs.form.validateField('dealers')
    },
    clearDealers () {
      this.formModel.dealers = []
      this.formModel.stores = []
      this.$refs.form.validateField('dealers')
    },
    openStoreSelect () {
      if (!this.formModel.dealers.length) {
        this.$message.warning('请先选择活动参与经销商')
        return
      }
      this.storeVisible = true
    },
    handleStoreSelected (list) {
      this.storeVisible = false
      this.formModel.stores = list || []
      this.$refs.form.validateField('stores')
    },
    clearStores () {
      this.formModel.stores = []
      this.$refs.form.validateField('stores')
    },
    clearSkus () {
      this.skuList = []
      this.formModel.skus = []
    },
    handleImportStore () {
      if (!this.formModel.dealers.length) {
        this.$message.warning('请先选择活动参与经销商')
      }
    },
    handleImportSkuSuccess (data) {
      const list = this.normalizeSkuList((data && data.list) || [])
      if (!list.length) {
        this.$message.warning('未导入到有效商品')
        return
      }
      // 导入为最新操作：同一商品/SKU 以本次导入为准，覆盖已有数据
      const importedSkuIds = new Set()
      const importedGoodsIds = new Set()
      list.forEach((item) => {
        if (item.item_id != null && item.item_id !== '') importedSkuIds.add(String(item.item_id))
        if (item.goods_id != null && item.goods_id !== '') importedGoodsIds.add(String(item.goods_id))
      })
      const existing = this.skuList.filter((item) => {
        const skuId = item.item_id != null ? item.item_id : item.sku_id
        const goodsId = item.goods_id != null ? item.goods_id : item.default_item_id
        if (goodsId != null && importedGoodsIds.has(String(goodsId))) return false
        if (skuId != null && importedSkuIds.has(String(skuId))) return false
        return true
      })
      const merged = existing.concat(list)
      this.skuList = merged
      this.formModel.skus = merged
      const count = data.imported_count != null ? data.imported_count : list.length
      this.$message.success(`成功导入 ${count} 个商品`)
    },
    handleImportStoreSuccess (data) {
      const list = (data && data.list) || []
      if (!list.length) {
        this.$message.warning('未导入到有效门店')
        return
      }
      const normalized = list.map((item) => {
        const distributorId = item.distributor_id != null
          ? item.distributor_id
          : (item.store_id != null ? item.store_id : item.id)
        return {
          ...item,
          distributor_id: distributorId,
          store_name_snapshot: item.store_name_snapshot || item.name || item.store_name || '',
          store_code: item.store_code || item.shop_code || ''
        }
      }).filter(item => item.distributor_id != null && item.distributor_id !== '')

      const idSet = new Set(
        this.formModel.stores
          .map(item => item.distributor_id)
          .filter(id => id != null && id !== '')
          .map(String)
      )
      const toAdd = normalized.filter((item) => {
        if (idSet.has(String(item.distributor_id))) return false
        idSet.add(String(item.distributor_id))
        return true
      })
      this.formModel.stores = this.formModel.stores.concat(toAdd)
      this.$refs.form.validateField('stores')
      const count = data.imported_count != null ? data.imported_count : toAdd.length
      this.$message.success(`成功导入 ${count} 家门店`)
    },
    handleCancel () {
      this.$router.go(-1)
    },
    backToList (tab) {
      let vm = this.$parent
      while (vm) {
        if (vm.$options.name === 'InterestFreeActivity' && typeof vm.switchTab === 'function') {
          vm.switchTab(tab)
          break
        }
        vm = vm.$parent
      }
      this.$router.go(-1)
    },
    buildSkuIds () {
      return this.skuList
        .map(item => Number(item.item_id != null ? item.item_id : item.itemId))
        .filter(id => !Number.isNaN(id))
    },
    buildSubmitPayload () {
      const [startTime, endTime] = this.formModel.activity_date || []
      const payload = {
        activity_name: this.formModel.activity_name,
        owner_type: this.formModel.owner_type,
        installment_terms: this.formModel.installment_terms,
        start_time: startTime,
        end_time: endTime,
        dealer_ids: this.formModel.dealers.map(item => Number(item.dealer_id)).filter(id => !Number.isNaN(id)),
        store_scope: this.formModel.store_type === 'all' ? 'all' : 'selected',
        store_ids: this.formModel.store_type === 'partial'
          ? this.formModel.stores.map(item => Number(item.distributor_id)).filter(id => !Number.isNaN(id))
          : [],
        sku_ids: this.buildSkuIds()
      }
      // 订单金额上下限、预算相关字段创建后不可改，仅创建时提交
      if (this.isCreate) {
        payload.min_order_amount = amountToApi(this.formModel.min_order_amount)
        payload.max_order_amount = amountToApi(this.formModel.max_order_amount)
        if (this.formModel.budget_amount != null && this.formModel.budget_amount !== '') {
          payload.budget_amount = amountToApi(this.formModel.budget_amount)
        }
        if (this.formModel.budget_alert_amount != null && this.formModel.budget_alert_amount !== '') {
          payload.budget_alert_amount = amountToApi(this.formModel.budget_alert_amount)
        }
      }
      return payload
    },
    async handleSubmit () {
      try {
        await this.$refs.form.validate()
      } catch (e) {
        return
      }
      try {
        this.submitting = true
        if (this.isCreate) {
          const payload = this.buildSubmitPayload()
          if (this.draftId) payload.draft_id = this.draftId
          await createActivity(payload)
          this.$message.success('创建成功')
          this.backToList('all')
        } else {
          await updateActivity(this.id, this.buildSubmitPayload())
          this.$message.success('保存成功')
          this.handleCancel()
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '保存失败'
        this.$message.error(msg)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.interest-free-operation {
  padding: 0 20px 90px;

  .amount-range {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;

    .inline-item {
      margin-bottom: 0;

      ::v-deep .el-form-item__content {
        display: flex;
        align-items: center;
      }
    }

    .amount-label,
    .amount-unit {
      margin: 0 8px;
      color: #606266;
      white-space: nowrap;
    }
  }

  .form-tip {
    margin: 8px 0 0;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
  }

  .selector-block {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .selector-count {
    color: #606266;
    font-size: 14px;
  }

  .import-actions {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 16px;
  }
}
</style>
